// Pure data — no icon component values, so this stays server-safe.

export const QUICK_ANSWER =
  "Proxidize and DataImpulse both start at $2/GB mobile and $1/GB residential for standard, country-level targeting — but that headline rate isn't the whole picture. DataImpulse bills state, city, ZIP, and ASN selection at twice its standard rate; Proxidize includes city and ASN targeting in its mobile plans at no extra charge. Choose Proxidize if you need that targeting, a flat rate that doesn't change with volume, or sticky mobile sessions that hold for days. Choose DataImpulse if you're consistently over 1,000 GB/month on standard targeting (its rate drops to $1.60/GB mobile and $0.80/GB residential), want bandwidth that never expires, or want to avoid a KYC requirement.";

export const KEY_TAKEAWAYS = [
  "Proxidize and DataImpulse both start at $2/GB mobile and $1/GB residential for standard, country-level targeting. This isn't a cheaper-price pitch — the difference is in volume pricing, targeting costs, and features.",
  "DataImpulse gets cheaper past 1,000 GB/month, on standard targeting. Its rate drops to $1.60/GB mobile and $0.80/GB residential above that threshold — but advanced targeting still doubles whichever rate applies, which narrows or erases that discount.",
  "DataImpulse bills advanced targeting at 2x the standard rate. Selecting a specific state, city, ZIP code, or ASN doubles the per-GB cost, per DataImpulse's published targeting docs; Proxidize includes city and ASN targeting in its $2/GB mobile rate at no extra charge.",
  "Proxidize holds sticky mobile sessions far longer. Up to 3 days per access point versus DataImpulse's 120-minute cap.",
  "DataImpulse doesn't publish a KYC requirement. Proxidize requires it after purchase, before you can use the proxies.",
  "DataImpulse covers more proxy types under one account. Residential, premium residential, mobile, and datacenter; Proxidize is mobile and residential only.",
];

export interface VerdictCard {
  provider: "proxidize" | "dataimpulse";
  title: string;
  points: string[];
}

export const VERDICT_CARDS: VerdictCard[] = [
  {
    provider: "proxidize",
    title: "Choose Proxidize if you need",
    points: [
      "City and ASN targeting included on mobile at no extra charge",
      "Sticky mobile sessions that hold for up to 3 days, not minutes, for stable account exits",
      "KYC verification required after purchase, before you can use the proxies — some buyers prefer that compliance posture",
      "Free sign-up with no credit card, instead of a paid minimum purchase to start",
      "A flat rate that doesn't change with volume, so your bill is predictable from the first GB",
    ],
  },
  {
    provider: "dataimpulse",
    title: "Choose DataImpulse if you need",
    points: [
      "A cheaper per-GB rate once you're consistently over 1,000 GB/month on either product",
      "Traffic that never expires on both mobile and residential, with no monthly reset",
      "No published KYC requirement at any stage, if avoiding identity verification altogether matters to your workflow",
      "A larger published mobile IP pool (16M+) across a broader set of countries (195+)",
      "One provider for residential, premium residential, mobile, and datacenter proxies",
    ],
  },
];

export interface ComparisonRow {
  feature: string;
  proxidize: string | boolean;
  dataimpulse: string | boolean;
}

export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    feature: "Best for",
    proxidize: "Predictable flat-rate billing, managed mobile control",
    dataimpulse: "Pay-as-you-go pricing that scales down at high volume",
  },
  {
    feature: "Product breadth",
    proxidize: "Mobile and residential live; ISP and datacenter coming soon",
    dataimpulse: "Residential, premium residential, mobile, and datacenter proxies",
  },
  {
    feature: "Mobile IP pool",
    proxidize: "Premium pooled mobile IPs, multiple countries",
    dataimpulse: "16M+ mobile IPs, 195 countries, 3G/4G/5G/LTE",
  },
  {
    feature: "Mobile country coverage",
    proxidize: "US, Canada, UK, Germany, India",
    dataimpulse: "195 countries",
  },
  {
    feature: "Mobile pricing model",
    proxidize: "Flat $2/GB at every volume (25 GB to 10,000 GB)",
    dataimpulse: "$2/GB under 1,000 GB, $1.60/GB at 1,000 GB and up",
  },
  {
    feature: "1,000 GB/month mobile",
    proxidize: "$2,000/mo ($2.00/GB)",
    dataimpulse: "$1,600/mo ($1.60/GB)",
  },
  {
    feature: "100 GB/month mobile",
    proxidize: "$200/mo ($2.00/GB)",
    dataimpulse: "$200/mo ($2.00/GB) — same standard rate below 1,000 GB, before targeting",
  },
  {
    feature: "Mobile targeting",
    proxidize: "City + ASN included in the $2/GB rate",
    dataimpulse:
      "Country selection/exclusion included in the standard rate; selecting a state, city, ZIP, or ASN is billed at 2x the standard rate",
  },
  {
    feature: "Mobile sticky sessions",
    proxidize: "Up to 3 days per access point",
    dataimpulse: "Up to 120 minutes",
  },
  {
    feature: "Mobile bandwidth expiration",
    proxidize: "Per GB bandwidth expires after one month",
    dataimpulse: "Traffic never expires",
  },
  {
    feature: "Residential network",
    proxidize: "Millions of IPs, 195+ countries, ethically sourced",
    dataimpulse: "90M+ IPs, 195+ countries, first-party sourced",
  },
  {
    feature: "Residential pricing model",
    proxidize: "Flat $1/GB under 2,000 GB, then $0.95–$0.90/GB",
    dataimpulse: "$1/GB under 1,000 GB, $0.80/GB at 1,000 GB and up",
  },
  {
    feature: "1,000 GB/month residential",
    proxidize: "$1,000/mo ($1.00/GB)",
    dataimpulse: "$800/mo ($0.80/GB)",
  },
  {
    feature: "100 GB/month residential",
    proxidize: "$100/mo ($1.00/GB)",
    dataimpulse: "$100/mo ($1.00/GB) — same rate below 1,000 GB",
  },
  {
    feature: "Unused residential bandwidth",
    proxidize: "Rolls over, doesn't expire",
    dataimpulse: "Never expires (pay-as-you-go, no monthly reset)",
  },
  {
    feature: "Minimum purchase",
    proxidize: "Free sign-up, no credit card; monthly plans start at 25 GB",
    dataimpulse: "$5 minimum purchase (2.5 GB mobile / 5 GB residential), pay-as-you-go",
  },
  {
    feature: "Billing model",
    proxidize: "Monthly per-GB plans",
    dataimpulse: "Pay-as-you-go per GB, no subscription",
  },
  {
    feature: "Sessions",
    proxidize: "Sticky or random per access point, custom rotation interval",
    dataimpulse: "Sticky (up to 120 min) or rotating",
  },
  {
    feature: "Protocols",
    proxidize: "HTTP(S), SOCKS5, HTTP/3 + QUIC",
    dataimpulse: "HTTP(S), SOCKS5",
  },
  {
    feature: "ISP (static residential) proxies",
    proxidize: "Not yet offered",
    dataimpulse: "Not offered",
  },
  {
    feature: "Datacenter proxies",
    proxidize: "Not yet offered",
    dataimpulse: "Available, from $0.45/GB at 1 TB",
  },
  {
    feature: "KYC requirement",
    proxidize: "Required after purchase, before you can use the proxies",
    dataimpulse: "Not published as a requirement",
  },
  { feature: "Self-serve signup", proxidize: true, dataimpulse: true },
  {
    feature: "Free / low-cost testing",
    proxidize: "Free sign-up, no credit card",
    dataimpulse: "$5 minimum purchase to start",
  },
  {
    feature: "Security certifications",
    proxidize: "SOC 2 Type II, ISO 27001",
    dataimpulse: "ISO 27001",
  },
  { feature: "G2 rating", proxidize: "4.7/5", dataimpulse: "4.8/5" },
  {
    feature: "Money-back guarantee",
    proxidize: "30 days (usage & policy limits)",
    dataimpulse: "7 days",
  },
];

export interface UseCaseFit {
  useCase: string;
  fit: "Proxidize" | "DataImpulse" | "Both";
  reason: string;
}

export const USE_CASE_FITS: UseCaseFit[] = [
  {
    useCase: "Predictable monthly mobile spend under 1,000 GB",
    fit: "Both",
    reason:
      "Both charge the same $2/GB standard rate below 1,000 GB, so cost is identical at typical volumes with country-level targeting. That parity ends the moment either side needs a specific state, city, ZIP, or ASN — DataImpulse doubles its rate for that, Proxidize doesn't.",
  },
  {
    useCase: "Mobile or residential usage consistently over 1,000 GB/month",
    fit: "DataImpulse",
    reason:
      "DataImpulse's rate drops to $1.60/GB mobile and $0.80/GB residential past 1,000 GB, undercutting Proxidize's flat and lightly-tiered rates at that scale — but only on standard targeting. Selecting a state, city, ZIP, or ASN still bills at double that rate, which narrows or erases the volume discount.",
  },
  {
    useCase: "Account workflows needing a long-held stable mobile exit",
    fit: "Proxidize",
    reason:
      "Proxidize sticky sessions hold for up to 3 days per access point, versus DataImpulse's 120-minute session cap.",
  },
  {
    useCase: "Granular mobile targeting without a per-GB surcharge",
    fit: "Proxidize",
    reason:
      "City and ASN targeting are included in Proxidize's $2/GB rate at no extra charge. DataImpulse bills the same targeting at double its standard rate, per its published targeting docs.",
  },
  {
    useCase: "One account for mobile, residential, and datacenter proxies",
    fit: "DataImpulse",
    reason:
      "DataImpulse also sells datacenter and premium residential proxies; Proxidize is currently mobile and residential only.",
  },
  {
    useCase: "Verification-sensitive or compliance-first buyers",
    fit: "Proxidize",
    reason:
      "Proxidize requires KYC verification from every customer. Buyers who need that compliance posture, or who are simply reassured by it, are better served here.",
  },
  {
    useCase: "Avoiding identity verification altogether",
    fit: "DataImpulse",
    reason:
      "DataImpulse doesn't publish a KYC requirement at any stage. Proxidize requires it after purchase, before you can use the proxies.",
  },
];

export interface MigrationStep {
  title: string;
  description: string;
}

export const MIGRATION_STEPS: MigrationStep[] = [
  {
    title: "Create a free account",
    description: "Sign up for free: no credit card, no minimum purchase, no sales call.",
  },
  {
    title: "Pick your Per GB plan",
    description:
      "Per-GB gives flexible access to the full pool, billed by the data you use, with monthly plans from 25 GB to 10,000 GB.",
  },
  {
    title: "Create an access point",
    description: "Choose a city or random rotation and set sticky or random IP behavior.",
  },
  {
    title: "Copy your credentials",
    description:
      "Credentials are generated live in HTTP or SOCKS5 format, with auto-generated cURL examples and formatted proxy list exports.",
  },
  {
    title: "Test before switching traffic",
    description:
      "Run your existing scraper or browser profile against the new endpoint with a small job and compare success rates side by side.",
  },
  {
    title: "Monitor usage in real time",
    description:
      "The built-in analytics dashboard shows bandwidth trends, top domains, and a live connection log per access point.",
  },
];

export const MIGRATION_CURL = `curl -x "http://USERNAME:PASSWORD@HOST:PORT" https://ipinfo.io`;
